import { useEffect, useState } from "react";

import { getAPIPayload } from "../../apis/getApi";

// Every servicer page rebuilds the sidebar. The last answers are remembered for
// this tab and user so the new page starts with the right menu; the server is
// still asked again in the background.
const sessionFlagKey = (name) => `${name}:${localStorage.getItem("uID") || ""}`;

/** true / false once known in this tab, null before the first answer. */
export const readSessionFlag = (name) => {
  try {
    const value = sessionStorage.getItem(sessionFlagKey(name));
    return value === null ? null : value === "1";
  } catch {
    return null;
  }
};

export const rememberSessionFlag = (name, value) => {
  try {
    sessionStorage.setItem(sessionFlagKey(name), value ? "1" : "0");
  } catch {
    // The menu still works; it just waits for the server on the next page.
  }
};

const CAR_PORTFOLIO_FLAG = "carPortfolioEnabled";

// The sidebar and the page both ask on every page change. Callers within this
// window, for the same user, share one request.
export const CAR_PORTFOLIO_REUSE_MS = 60000;
let sharedCarPortfolioRequest = null;

export const loadCarPortfolioEnabled = () => {
  const userId = localStorage.getItem("uID") || "";
  const now = Date.now();
  if (
    sharedCarPortfolioRequest &&
    sharedCarPortfolioRequest.userId === userId &&
    now - sharedCarPortfolioRequest.startedAt < CAR_PORTFOLIO_REUSE_MS
  ) {
    return sharedCarPortfolioRequest.promise;
  }

  const promise = getAPIPayload({
    url: "service-provider/car-portfolio",
    data: { per_page: 1 },
    token: true,
  });
  const entry = { userId, startedAt: now, promise };
  sharedCarPortfolioRequest = entry;

  // A failed answer is not reused; the next caller asks again.
  const forget = () => {
    if (sharedCarPortfolioRequest === entry) {
      sharedCarPortfolioRequest = null;
    }
  };
  promise.then((response) => {
    if (!response?.success) forget();
  }, forget);

  return promise;
};

export const useCarPortfolioEnabled = () => {
  const [remembered] = useState(() => readSessionFlag(CAR_PORTFOLIO_FLAG));
  const [carPortfolioEnabled, setCarPortfolioEnabled] = useState(
    remembered === true,
  );
  const [loading, setLoading] = useState(remembered === null);

  useEffect(() => {
    let cancelled = false;

    loadCarPortfolioEnabled()
      .then((response) => {
        if (!cancelled) {
          const enabled = response?.data?.enabled === true;
          setCarPortfolioEnabled(enabled);
          rememberSessionFlag(CAR_PORTFOLIO_FLAG, enabled);
          if (enabled) {
            localStorage.setItem("signup_source", "car");
          }
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Keep a remembered answer rather than flipping the menu on a failed call.
          if (remembered === null) {
            setCarPortfolioEnabled(false);
          }
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [remembered]);

  return { carPortfolioEnabled, loading };
};
